<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div v-if="loading" class="pd-card pd-state">
          <div class="pd-spinner"></div>
          <span>Loading item details...</span>
        </div>

        <div v-else-if="!item" class="pd-card pd-state">
          <strong>Item not found</strong>
          <span>It may have been deleted, or the link is incorrect.</span>
          <router-link to="/productlist" class="btn btn-submit mt-2">Back to items</router-link>
        </div>

        <template v-else>
          <!-- Hero -->
          <section class="pd-card pd-hero">
            <div class="pd-media">
              <img v-if="activeImage" :src="activeImage.preview_url || activeImage.url" :alt="activeImage.alt_text || item.itemname" />
              <span v-else class="pd-initials">{{ initials }}</span>
              <div v-if="images.length > 1" class="pd-thumbs">
                <button
                  v-for="image in images.slice(0, 4)"
                  :key="image.image_id"
                  type="button"
                  class="pd-thumb"
                  :class="{ active: activeImage && activeImage.image_id === image.image_id }"
                  @click="activeImageId = image.image_id"
                >
                  <img :src="image.preview_url || image.url" :alt="image.alt_text || item.itemname" />
                </button>
              </div>
            </div>

            <div class="pd-hero-body">
              <div class="pd-chips">
                <span v-if="item.itemcode" class="pd-chip pd-chip-code">{{ item.itemcode }}</span>
                <span v-if="categoryName" class="pd-chip">{{ categoryName }}</span>
                <span class="pd-chip" :class="`pd-chip-${stockStatus.key}`">{{ stockStatus.label }}</span>
                <span v-if="item.batchenabled" class="pd-chip pd-chip-muted">Batch tracked</span>
                <span v-if="item.expiryenabled" class="pd-chip pd-chip-muted">Expiry tracked</span>
                <span v-if="item.is_online" class="pd-chip pd-chip-online">Sold online</span>
              </div>
              <h2 class="pd-name">{{ item.itemname }}</h2>
              <p class="pd-desc">{{ item.itemdescription || "No description added." }}</p>
              <div class="pd-meta">
                <span v-if="item.barcode"><i class="fa fa-barcode" aria-hidden="true"></i> {{ item.barcode }}</span>
                <span v-if="item.rackno"><i class="fa fa-th-large" aria-hidden="true"></i> Rack {{ item.rackno }}</span>
                <span v-if="item.hsncode"><i class="fas fa-file-alt" aria-hidden="true"></i> HSN {{ item.hsncode }}</span>
                <span v-if="item.createdat"><i class="far fa-calendar" aria-hidden="true"></i> Added {{ formatDate(item.createdat) }}</span>
              </div>
            </div>

            <div class="pd-hero-actions">
              <button v-if="canEdit" type="button" class="btn btn-submit" @click="editProduct">
                <i class="fas fa-pen me-1" aria-hidden="true"></i> Edit Item
              </button>
              <router-link to="/productlist" class="btn pd-btn-light">Back to items</router-link>
            </div>
          </section>

          <!-- KPIs -->
          <div class="pd-kpis">
            <div class="pd-card pd-kpi">
              <span class="pd-kpi-label">Available stock</span>
              <strong class="pd-kpi-value" :class="`text-${stockStatus.key}`">
                {{ formatNum(item.available) || 0 }} <small>{{ item.unit }}</small>
              </strong>
              <div class="pd-bar" :title="stockBarTitle">
                <span :class="`bar-${stockStatus.key}`" :style="{ width: `${stockBarPercent}%` }"></span>
              </div>
              <span class="pd-kpi-sub">
                Min {{ formatNum(minStock) || 0 }}<template v-if="item.maxstock"> &middot; Max {{ formatNum(item.maxstock) }}</template>
                <template v-if="showAllLocationStock"> &middot; All locations {{ formatNum(item.totalavailable) }}</template>
              </span>
            </div>

            <div class="pd-card pd-kpi">
              <span class="pd-kpi-label">Stock value</span>
              <strong class="pd-kpi-value">{{ money(stockValue) || "Rs. 0" }}</strong>
              <span class="pd-kpi-sub">{{ batchesInStock.length ? "Based on cost of each batch in stock" : "Available stock × purchase rate" }}</span>
            </div>

            <div class="pd-card pd-kpi">
              <span class="pd-kpi-label">Selling price</span>
              <strong class="pd-kpi-value">{{ money(item.unitprice) || "-" }}</strong>
              <span class="pd-kpi-sub">
                <template v-if="Number(item.mrp) > 0">MRP {{ money(item.mrp) }}<template v-if="mrpDiscount > 0"> &middot; {{ formatNum(mrpDiscount) }}% below MRP</template></template>
                <template v-else>No MRP set</template>
                &middot; {{ item.istaxinclusive ? "incl. tax" : "excl. tax" }}
              </span>
            </div>

            <div class="pd-card pd-kpi">
              <span class="pd-kpi-label">Margin per {{ item.unit || "unit" }}</span>
              <strong class="pd-kpi-value" :class="margin.amount < 0 ? 'text-out' : 'text-ok'">
                {{ margin.available ? money(margin.amount) : "-" }}
              </strong>
              <span class="pd-kpi-sub">
                <template v-if="margin.available">{{ formatNum(margin.percent) }}% of selling price, before tax</template>
                <template v-else>Set a purchase rate to see margin</template>
              </span>
            </div>
          </div>

          <div v-if="expiryAlerts.expired || expiryAlerts.soon" class="pd-alert">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span>
              <template v-if="expiryAlerts.expired">{{ formatNum(expiryAlerts.expired) }} {{ item.unit }} in stock {{ expiryAlerts.expired === 1 ? "has" : "have" }} expired.</template>
              <template v-if="expiryAlerts.soon"> {{ formatNum(expiryAlerts.soon) }} {{ item.unit }} expire within 30 days.</template>
              See "Batches in stock" below.
            </span>
          </div>

          <div class="pd-layout">
            <!-- Activity tabs -->
            <section class="pd-card pd-activity">
              <div class="pd-tabs" role="tablist">
                <button
                  v-for="tab in tabs"
                  :key="tab.key"
                  type="button"
                  role="tab"
                  class="pd-tab"
                  :class="{ active: activeTab === tab.key }"
                  :aria-selected="activeTab === tab.key"
                  @click="activeTab = tab.key"
                >
                  {{ tab.label }}
                  <span v-if="tab.count !== undefined" class="pd-tab-count">{{ tab.count }}</span>
                </button>
              </div>

              <!-- Batches -->
              <div v-if="activeTab === 'batches'">
                <div v-if="historyLoading" class="pd-empty">Loading batches...</div>
                <div v-else-if="!batchesInStock.length" class="pd-empty">No batches with stock at this location.</div>
                <div v-else class="table-responsive">
                  <table class="pd-table">
                    <thead>
                      <tr>
                        <th>Batch</th>
                        <th>Expiry</th>
                        <th class="num">In stock</th>
                        <th class="num">Cost</th>
                        <th class="num">MRP</th>
                        <th>Supplier</th>
                        <th>Purchased</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="batch in batchesInStock" :key="batch.purchaseitemid">
                        <td class="strong">{{ batch.batchno || "-" }}</td>
                        <td>
                          <span v-if="batch.expirydate" class="pd-exp" :class="`exp-${expiryState(batch.expirydate)}`">
                            {{ formatDate(batch.expirydate) }}
                            <small v-if="expiryState(batch.expirydate) === 'expired'">Expired</small>
                            <small v-else-if="expiryState(batch.expirydate) === 'soon'">{{ daysUntil(batch.expirydate) }}d left</small>
                          </span>
                          <span v-else class="muted">-</span>
                        </td>
                        <td class="num strong">{{ formatNum(batch.remainingqty) }}</td>
                        <td class="num">{{ money(batch.netpurchaserate || batch.purchaseprice) }}</td>
                        <td class="num">{{ money(batch.mrp) || "-" }}</td>
                        <td>{{ batch.vendor || "-" }}</td>
                        <td>{{ formatDate(batch.invoicedate || batch.purchasedate) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Purchases -->
              <div v-else-if="activeTab === 'purchases'">
                <div v-if="historyLoading" class="pd-empty">Loading purchases...</div>
                <div v-else-if="!purchases.length" class="pd-empty">This item has not been purchased yet.</div>
                <div v-else class="table-responsive">
                  <table class="pd-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Bill</th>
                        <th>Supplier</th>
                        <th class="num">Qty</th>
                        <th class="num">Cost</th>
                        <th class="num">MRP</th>
                        <th>Batch</th>
                        <th class="num">Left</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in purchases" :key="row.purchaseitemid">
                        <td>{{ formatDate(row.invoicedate || row.purchasedate) }}</td>
                        <td>
                          <router-link v-if="canViewPurchases" :to="{ path: '/purchase-details', query: { id: row.purchaseid } }" class="pd-link">
                            {{ row.billno || row.invoiceno || "View" }}
                          </router-link>
                          <span v-else>{{ row.billno || row.invoiceno || "-" }}</span>
                          <small v-if="row.invoiceno && row.billno" class="pd-sub">Inv {{ row.invoiceno }}</small>
                        </td>
                        <td>{{ row.vendor || "-" }}</td>
                        <td class="num">
                          {{ formatNum(row.quantity) }}
                          <small v-if="Number(row.freequantity) > 0" class="pd-sub">+{{ formatNum(row.freequantity) }} free</small>
                        </td>
                        <td class="num">{{ money(row.netpurchaserate || row.purchaseprice) }}</td>
                        <td class="num">{{ money(row.mrp) || "-" }}</td>
                        <td>{{ row.batchno || "-" }}</td>
                        <td class="num" :class="{ muted: !Number(row.remainingqty) }">{{ formatNum(row.remainingqty) || 0 }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-if="purchaseTotal > purchases.length" class="pd-foot-note">
                    Showing the latest {{ purchases.length }} of {{ purchaseTotal }} purchases.
                  </p>
                </div>
              </div>

              <!-- Movements -->
              <div v-else-if="activeTab === 'movements'">
                <div v-if="movementsLoading" class="pd-empty">Loading stock movements...</div>
                <template v-else>
                  <div class="pd-move-summary">
                    <div><span>Opening (90 days ago)</span><strong>{{ formatNum(movementSummary.opening) || 0 }}</strong></div>
                    <div><span>Stock in</span><strong class="text-ok">+{{ formatNum(movementSummary.in) || 0 }}</strong></div>
                    <div><span>Stock out</span><strong class="text-out">-{{ formatNum(movementSummary.out) || 0 }}</strong></div>
                    <div><span>Closing</span><strong>{{ formatNum(movementSummary.closing) || 0 }}</strong></div>
                  </div>
                  <div v-if="!movements.length" class="pd-empty">No stock movements in the last 90 days.</div>
                  <div v-else class="table-responsive">
                    <table class="pd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Reference</th>
                          <th class="num">In</th>
                          <th class="num">Out</th>
                          <th class="num">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in movements" :key="row.rowkey">
                          <td>{{ formatDateTime(row.date) }}</td>
                          <td><span class="pd-type" :class="`type-${row.referencekind || 'other'}`">{{ movementLabel(row.type) }}</span></td>
                          <td>
                            <router-link v-if="referenceLink(row)" :to="referenceLink(row)" class="pd-link">{{ row.refno || "View" }}</router-link>
                            <span v-else>{{ row.refno || "-" }}</span>
                          </td>
                          <td class="num text-ok">{{ row.in ? `+${formatNum(row.in)}` : "" }}</td>
                          <td class="num text-out">{{ row.out ? `-${formatNum(row.out)}` : "" }}</td>
                          <td class="num strong">{{ formatNum(row.balance) || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p class="pd-foot-note">Latest {{ movements.length }} movements from the last 90 days.</p>
                  </div>
                </template>
              </div>
            </section>

            <!-- Info panels -->
            <aside class="pd-side">
              <section v-for="panel in infoPanels" :key="panel.title" class="pd-card pd-panel">
                <h5>{{ panel.title }}</h5>
                <dl>
                  <div v-for="row in panel.rows" :key="row.label" class="pd-dl-row">
                    <dt>{{ row.label }}</dt>
                    <dd :class="{ muted: !row.value }">{{ row.value || "Not set" }}</dd>
                  </div>
                </dl>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const MOVEMENT_LABELS = {
  OPENING: "Opening stock",
  OPENING_STOCK: "Opening stock",
  PURCHASE: "Purchase",
  PURCHASE_ADJUSTMENT: "Purchase edit",
  PURCHASE_REVERSAL: "Purchase reversed",
  SALE: "Sale",
  SALE_REVERSAL: "Sale reversed",
  PURCHASE_RETURN: "Purchase return",
  PURCHASE_RETURN_CXL: "Purchase return cancelled",
  SALES_RETURN: "Sales return",
  SALES_RETURN_CANCEL: "Sales return cancelled",
  DAMAGED: "Damaged",
  DAMAGED_CANCEL: "Damaged cancelled",
  ADJUSTMENT_IN: "Stock added",
  ADJUSTMENT_OUT: "Stock removed",
  ADJUSTMENT_CANCEL: "Adjustment cancelled",
};
const MOVEMENT_LIMIT = 25;
const MOVEMENT_DAYS = 90;

function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default {
  name: "product-details",
  data() {
    return {
      title: "Item Details",
      title1: "Stock, pricing and history of an item",
      item: null,
      loading: false,
      images: [],
      activeImageId: null,
      categories: [],
      purchases: [],
      purchaseTotal: 0,
      historyLoading: false,
      movements: [],
      movementSummary: { opening: 0, in: 0, out: 0, closing: 0 },
      movementsLoading: false,
      activeTab: "batches",
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters.hasPermission("inventory.edit");
    },
    canViewPurchases() {
      return this.$store.getters.hasPermission("purchase.view");
    },
    canViewSales() {
      return this.$store.getters.hasPermission("sales.view");
    },
    canViewMovements() {
      return this.$store.getters.hasPermission("reports.stock.view");
    },
    tabs() {
      const tabs = [
        { key: "batches", label: "Batches in stock", count: this.historyLoading ? undefined : this.batchesInStock.length },
        { key: "purchases", label: "Purchase history", count: this.historyLoading ? undefined : this.purchaseTotal },
      ];
      if (this.canViewMovements) {
        tabs.push({ key: "movements", label: "Stock movements" });
      }
      return tabs;
    },
    initials() {
      return String(this.item?.itemname || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("");
    },
    activeImage() {
      if (!this.images.length) return null;
      return (
        this.images.find((image) => image.image_id === this.activeImageId) ||
        this.images.find((image) => image.is_primary) ||
        this.images[0]
      );
    },
    categoryName() {
      if (!this.item?.itemcategory) return "";
      const match = this.categories.find((category) => String(category.categoryid) === String(this.item.itemcategory));
      return match ? match.categoryname : this.item.itemcategory;
    },
    minStock() {
      return Number(this.item?.minstock ?? this.item?.threshold ?? 0) || 0;
    },
    stockStatus() {
      const available = Number(this.item?.available) || 0;
      if (available <= 0) return { key: "out", label: "Out of stock" };
      if (available <= this.minStock) return { key: "low", label: "Low stock" };
      return { key: "ok", label: "In stock" };
    },
    stockBarPercent() {
      const available = Math.max(Number(this.item?.available) || 0, 0);
      const target = Number(this.item?.maxstock) || this.minStock * 2 || available || 1;
      return Math.min(100, Math.round((available / target) * 100));
    },
    stockBarTitle() {
      return this.item?.maxstock ? "Stock compared to max stock" : "Stock compared to twice the minimum stock";
    },
    showAllLocationStock() {
      const total = Number(this.item?.totalavailable);
      return Number.isFinite(total) && total !== Number(this.item?.available);
    },
    batchesInStock() {
      return this.purchases
        .filter((row) => Number(row.remainingqty) > 0)
        .sort((a, b) => {
          if (!a.expirydate) return 1;
          if (!b.expirydate) return -1;
          return new Date(a.expirydate) - new Date(b.expirydate);
        });
    },
    stockValue() {
      if (this.batchesInStock.length) {
        return this.batchesInStock.reduce(
          (sum, row) => sum + Number(row.remainingqty || 0) * Number(row.netpurchaserate || row.purchaseprice || 0),
          0
        );
      }
      return (Number(this.item?.available) || 0) * (Number(this.item?.purchaserate) || 0);
    },
    mrpDiscount() {
      const mrp = Number(this.item?.mrp) || 0;
      const price = Number(this.item?.unitprice) || 0;
      if (!mrp || price >= mrp) return 0;
      return ((mrp - price) / mrp) * 100;
    },
    margin() {
      const purchase = Number(this.item?.purchaserate) || 0;
      if (!purchase) return { available: false, amount: 0, percent: 0 };
      let selling = Number(this.item?.unitprice) || 0;
      const taxPercent = Number(this.item?.taxpercent) || 0;
      if (this.item?.istaxinclusive && taxPercent > 0) {
        selling = (selling * 100) / (100 + taxPercent);
      }
      const amount = selling - purchase;
      return { available: true, amount, percent: selling ? (amount / selling) * 100 : 0 };
    },
    expiryAlerts() {
      return this.batchesInStock.reduce(
        (acc, row) => {
          const state = this.expiryState(row.expirydate);
          if (state === "expired") acc.expired += Number(row.remainingqty || 0);
          if (state === "soon") acc.soon += Number(row.remainingqty || 0);
          return acc;
        },
        { expired: 0, soon: 0 }
      );
    },
    infoPanels() {
      if (!this.item) return [];
      const item = this.item;
      return [
        {
          title: "Pricing",
          rows: [
            { label: "Purchase rate", value: this.money(item.purchaserate) },
            { label: "Selling price", value: this.money(item.unitprice) },
            { label: "MRP", value: this.money(item.mrp) },
            { label: "Wholesale rate", value: this.money(item.wholesalerate) },
            ...(item.is_online || item.online_price ? [{ label: "Online price", value: this.money(item.online_price) }] : []),
          ],
        },
        {
          title: "Tax",
          rows: [
            { label: "HSN code", value: item.hsncode },
            { label: "Tax", value: item.taxpercent !== null && item.taxpercent !== undefined ? `${this.formatNum(item.taxpercent) || 0}% ${item.taxtype || "GST"}` : "" },
            { label: "CGST / SGST", value: this.splitRate(item.cgstrate, item.sgstrate) },
            { label: "IGST", value: this.percent(item.igstrate) },
            { label: "Price includes tax", value: item.istaxinclusive ? "Yes" : "No" },
          ],
        },
        {
          title: "Stock settings",
          rows: [
            { label: "Unit", value: item.unit },
            { label: "Min stock", value: this.formatNum(this.minStock) },
            { label: "Max stock", value: this.formatNum(item.maxstock) },
            { label: "Rack no.", value: item.rackno },
            { label: "Barcode", value: item.barcode },
          ],
        },
        {
          title: "Units of measure",
          rows: [
            { label: "Upper unit", value: item.uom_upper },
            { label: "Lower unit", value: item.uom_lower },
            {
              label: "Conversion",
              value: item.conversionfactor && item.uom_upper && item.uom_lower
                ? `1 ${item.uom_upper} = ${this.formatNum(item.conversionfactor)} ${item.uom_lower}`
                : this.formatNum(item.conversionfactor),
            },
          ],
        },
      ];
    },
  },
  watch: {
    "$route.query.id"(newId, oldId) {
      if (newId && newId !== oldId) this.loadAll();
    },
  },
  methods: {
    async loadAll() {
      await this.getItemDetails();
      if (!this.item) return;
      this.loadImages();
      this.loadCategories();
      this.loadPurchases();
      if (this.canViewMovements) this.loadMovements();
    },
    async getItemDetails() {
      const itemId = this.$route.query.id;
      if (!itemId) {
        this.item = null;
        return;
      }

      this.loading = true;
      try {
        const response = await axios.get(`/v1/items/${itemId}`);
        this.item = response.data && response.data.itemid ? response.data : null;
      } catch (error) {
        console.error("Error fetching item details:", error);
        this.item = null;
        if (error.response?.status !== 404) {
          Swal.fire({
            title: "Failed to load item",
            text: error.response?.data?.detail || error.message,
            icon: "error",
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async loadImages() {
      try {
        const response = await axios.get(`/v1/items/${this.item.itemid}/images`);
        this.images = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.images = [];
      }
    },
    async loadCategories() {
      try {
        const response = await axios.get("/v1/category");
        this.categories = Array.isArray(response.data) ? response.data : response.data?.items || [];
      } catch (error) {
        this.categories = [];
      }
    },
    async loadPurchases() {
      this.historyLoading = true;
      try {
        const response = await axios.get(`/v1/items/${this.item.itemid}/purchases`, { params: { limit: 50 } });
        this.purchases = response.data?.items || [];
        this.purchaseTotal = response.data?.total || this.purchases.length;
      } catch (error) {
        this.purchases = [];
        this.purchaseTotal = 0;
      } finally {
        this.historyLoading = false;
      }
    },
    async loadMovements() {
      this.movementsLoading = true;
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - MOVEMENT_DAYS);
      const params = {
        itemid: this.item.itemid,
        from_date: isoDate(from),
        to_date: isoDate(today),
        page_size: MOVEMENT_LIMIT,
      };
      try {
        // The ledger is sorted oldest first, so read the total and then fetch the last page(s).
        const url = "/v1/stockledgerreport";
        const first = await axios.get(url, { params: { ...params, page: 1 } });
        const total = Number(first.data?.total || 0);
        this.movementSummary = { ...this.movementSummary, ...(first.data?.summary || {}) };
        let rows = first.data?.items || [];
        const lastPage = Math.ceil(total / MOVEMENT_LIMIT);
        if (lastPage > 1) {
          const pages = [lastPage - 1, lastPage];
          const responses = await Promise.all(pages.map((page) => (
            page === 1 ? first : axios.get(url, { params: { ...params, page } })
          )));
          rows = responses.flatMap((response) => response.data?.items || []);
        }
        this.movements = rows.slice(-MOVEMENT_LIMIT).reverse();
      } catch (error) {
        this.movements = [];
      } finally {
        this.movementsLoading = false;
      }
    },
    editProduct() {
      if (!this.item) return;
      this.$router.push({ path: "/editproduct", query: { id: this.item.itemid } });
    },
    movementLabel(type) {
      return MOVEMENT_LABELS[type] || String(type || "-").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
    },
    referenceLink(row) {
      if (!row.referenceid) return null;
      if (row.referencekind === "sale" && this.canViewSales) return { path: "/sales-details", query: { id: row.referenceid } };
      if (row.referencekind === "purchase" && this.canViewPurchases) return { path: "/purchase-details", query: { id: row.referenceid } };
      if (row.referencekind === "stock_adjustment") return { path: "/stockadjustment", query: { id: row.referenceid } };
      return null;
    },
    daysUntil(value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Math.ceil((new Date(value) - today) / 86400000);
    },
    expiryState(value) {
      if (!value) return "none";
      const days = this.daysUntil(value);
      if (days < 0) return "expired";
      if (days <= 30) return "soon";
      return "ok";
    },
    formatNum(value) {
      if (value === null || value === undefined || value === "") return "";
      const number = parseFloat(value);
      if (Number.isNaN(number)) return "";
      return number.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    money(value) {
      const formatted = this.formatNum(value);
      return formatted ? `Rs. ${formatted}` : "";
    },
    percent(value) {
      const formatted = this.formatNum(value);
      return formatted ? `${formatted}%` : "";
    },
    splitRate(cgst, sgst) {
      if (!this.formatNum(cgst) && !this.formatNum(sgst)) return "";
      return `${this.formatNum(cgst) || 0}% + ${this.formatNum(sgst) || 0}%`;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    },
  },
  beforeMount() {
    this.loadAll();
  },
};
</script>

<style scoped>
.pd-card {
  background: #fff;
  border: 1px solid #e9ecf2;
  border-radius: 12px;
}
.pd-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
}
.pd-state strong { color: #111827; font-size: 16px; }
.pd-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top-color: #FF9F43;
  border-radius: 50%;
  animation: pd-spin 0.7s linear infinite;
}
@keyframes pd-spin { to { transform: rotate(360deg); } }

/* hero */
.pd-hero {
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr) auto;
  gap: 22px;
  padding: 20px;
  margin-bottom: 16px;
}
.pd-media { display: flex; flex-direction: column; gap: 8px; }
.pd-media > img,
.pd-initials {
  width: 132px;
  height: 132px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #eef0f4;
}
.pd-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fff4e8, #ffe2c4);
  color: #c2410c;
  font-size: 40px;
  font-weight: 700;
}
.pd-thumbs { display: flex; gap: 6px; }
.pd-thumb {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.pd-thumb.active { border-color: #FF9F43; }
.pd-thumb img { width: 100%; height: 100%; object-fit: cover; }
.pd-hero-body { min-width: 0; }
.pd-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.pd-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
}
.pd-chip-code { background: #eef2ff; color: #4338ca; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.pd-chip-ok { background: #dcfce7; color: #15803d; }
.pd-chip-low { background: #ffedd5; color: #c2410c; }
.pd-chip-out { background: #fee2e2; color: #dc2626; }
.pd-chip-muted { background: #f9fafb; color: #6b7280; border: 1px solid #e5e7eb; }
.pd-chip-online { background: #e0f2fe; color: #0369a1; }
.pd-name { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #111827; overflow-wrap: anywhere; }
.pd-desc { margin: 0 0 12px; color: #6b7280; font-size: 14px; max-width: 760px; }
.pd-meta { display: flex; flex-wrap: wrap; gap: 8px 20px; color: #4b5563; font-size: 13px; }
.pd-meta i { color: #9ca3af; margin-right: 4px; }
.pd-hero-actions { display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
.pd-hero-actions .btn { white-space: nowrap; min-width: 150px; }
.pd-btn-light { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.pd-btn-light:hover { border-color: #FF9F43; color: #c2410c; }

/* kpis */
.pd-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}
.pd-kpi { padding: 16px 18px; display: flex; flex-direction: column; gap: 6px; }
.pd-kpi-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
.pd-kpi-value { font-size: 22px; font-weight: 700; color: #111827; line-height: 1.2; }
.pd-kpi-value small { font-size: 13px; font-weight: 500; color: #6b7280; }
.pd-kpi-sub { font-size: 12px; color: #6b7280; }
.pd-bar { height: 6px; border-radius: 999px; background: #f1f2f4; overflow: hidden; }
.pd-bar span { display: block; height: 100%; border-radius: 999px; transition: width 0.3s ease; }
.bar-ok { background: #22c55e; }
.bar-low { background: #fb923c; }
.bar-out { background: #ef4444; }
.text-ok { color: #15803d !important; }
.text-low { color: #c2410c !important; }
.text-out { color: #dc2626 !important; }

.pd-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
}
.pd-alert i { margin-top: 2px; }

/* layout */
.pd-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}
.pd-activity { padding: 6px 18px 18px; min-width: 0; }
.pd-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #eef0f4;
  margin-bottom: 14px;
  overflow-x: auto;
}
.pd-tab {
  border: 0;
  background: transparent;
  padding: 12px 12px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
}
.pd-tab:hover { color: #111827; }
.pd-tab.active { color: #c2410c; border-bottom-color: #FF9F43; }
.pd-tab-count {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
}
.pd-tab.active .pd-tab-count { background: #fff4e8; color: #c2410c; }

.pd-table { width: 100%; border-collapse: collapse; }
.pd-table th,
.pd-table td {
  padding: 10px 10px;
  border-bottom: 1px solid #f1f2f4;
  font-size: 13px;
  vertical-align: middle;
  color: #374151;
}
.pd-table th {
  background: #f9fafb;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.pd-table tbody tr:hover td { background: #fffaf5; }
.pd-table .num { text-align: right; white-space: nowrap; }
.pd-table .strong { font-weight: 600; color: #111827; }
.pd-sub { display: block; font-size: 11px; color: #9ca3af; }
.muted { color: #9ca3af !important; }
.pd-link { color: #c2410c; font-weight: 600; }
.pd-link:hover { text-decoration: underline; }
.pd-exp { display: inline-flex; flex-direction: column; }
.pd-exp small { font-size: 11px; font-weight: 600; }
.exp-expired { color: #dc2626; }
.exp-soon { color: #c2410c; }
.pd-type {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: #f3f4f6;
  color: #374151;
  white-space: nowrap;
}
.type-sale { background: #e0f2fe; color: #0369a1; }
.type-purchase { background: #dcfce7; color: #15803d; }
.type-sales_return { background: #fef3c7; color: #b45309; }
.type-purchase_return { background: #fce7f3; color: #be185d; }
.pd-empty {
  padding: 28px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}
.pd-foot-note { margin: 10px 0 0; font-size: 12px; color: #9ca3af; }
.pd-move-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.pd-move-summary div {
  padding: 10px 12px;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  background: #fafbfc;
}
.pd-move-summary span { display: block; font-size: 11px; color: #6b7280; margin-bottom: 2px; }
.pd-move-summary strong { font-size: 16px; color: #111827; }

/* side panels */
.pd-side { display: flex; flex-direction: column; gap: 14px; }
.pd-panel { padding: 16px 18px; }
.pd-panel h5 { margin: 0 0 10px; font-size: 14px; font-weight: 700; color: #111827; }
.pd-panel dl { margin: 0; }
.pd-dl-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px dashed #eef0f4;
  font-size: 13px;
}
.pd-dl-row:last-child { border-bottom: 0; }
.pd-dl-row dt { font-weight: 400; color: #6b7280; }
.pd-dl-row dd { margin: 0; font-weight: 600; color: #111827; text-align: right; overflow-wrap: anywhere; }
.pd-dl-row dd.muted { font-weight: 400; }

@media (max-width: 1200px) {
  .pd-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pd-layout { grid-template-columns: 1fr; }
  .pd-side { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .pd-hero { grid-template-columns: 96px minmax(0, 1fr); }
  .pd-media > img, .pd-initials { width: 96px; height: 96px; font-size: 30px; }
  .pd-hero-actions { grid-column: 1 / -1; flex-direction: row; }
  .pd-hero-actions .btn { flex: 1; min-width: 0; }
  .pd-move-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pd-side { grid-template-columns: 1fr; }
}
@media (max-width: 576px) {
  .pd-kpis { grid-template-columns: 1fr; }
  .pd-hero { grid-template-columns: 1fr; }
  .pd-name { font-size: 20px; }
}
</style>
